import React from "react";
import { SITE, WHATSAPP_LINK, EMAIL_LINK } from "../lib/site-data";
import {
  MessageCircle,
  Mail,
  Phone,
  ArrowUpRight,
  ClipboardList,
  ExternalLink,
} from "lucide-react";

export default function SignUp() {
  const studentImage = "/illustrations/signup-student.png";

  const whatsappMsg = `Hi ${SITE.name}, I'd like to sign up my child for tuition.

Child's name:
Primary level (P3 / P4 / P5 / P6):
Subject(s) (English / Math / Both):
Preferred slot from your schedule:

Thanks!`;

  const emailBody = `Hi ${SITE.name},\n\nI'd like to sign up my child for tuition.\n\nChild's name:\nPrimary level (P3 / P4 / P5 / P6):\nSubject(s) (English / Math / Both):\nPreferred slot from your schedule:\n\nThanks!`;

  return (
    <section
      id="signup"
      className="py-20 lg:py-28 bg-[#FAFAFA]"
      data-testid="signup-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-[#1E3A8A] rounded-3xl overflow-hidden relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 items-stretch">
            <div className="lg:col-span-7 p-10 sm:p-14 lg:p-16 text-white relative">
              <div className="text-sm font-medium text-[#D97706] uppercase tracking-widest mb-3">
                Sign Up
              </div>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-tight">
                Secure your child&apos;s spot today.
              </h2>
              <p
                className="mt-5 text-white/85 leading-relaxed max-w-xl"
                data-testid="signup-intro"
              >
                To begin your child&apos;s journey with The Tutor&apos;s Table,
                please fill in the registration form below. Feel free to let us
                know via WhatsApp message if you have any questions — we&apos;ll
                be happy to assist!
              </p>

              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <a
                  href={SITE.registrationFormUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-[#D97706] hover:bg-[#B45309] text-white px-7 py-4 rounded-full font-semibold transition-all shadow-md hover:shadow-lg"
                  data-testid="signup-form-cta"
                >
                  <ClipboardList className="w-5 h-5" />
                  Open registration form
                  <ExternalLink className="w-4 h-4" />
                </a>
                <a
                  href={WHATSAPP_LINK(whatsappMsg)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white border border-white/30 px-7 py-4 rounded-full font-semibold transition-all backdrop-blur-sm"
                  data-testid="signup-whatsapp-cta"
                >
                  <MessageCircle className="w-5 h-5" />
                  Ask on WhatsApp
                  <ArrowUpRight className="w-4 h-4" />
                </a>
              </div>

              <p className="mt-5 text-xs text-white/60 max-w-md" data-testid="signup-form-helper">
                Opens in a new tab · Google Forms
              </p>

              <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-lg">
                <a
                  href={WHATSAPP_LINK("Hi, I'd like to chat.")}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-4 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors"
                  data-testid="signup-contact-whatsapp"
                >
                  <Phone className="w-5 h-5 text-[#D97706]" />
                  <div>
                    <div className="text-xs text-white/60">WhatsApp</div>
                    <div className="text-sm font-medium">
                      {SITE.whatsappNumberDisplay}
                    </div>
                  </div>
                </a>
                <a
                  href={EMAIL_LINK("Tuition Enrolment Enquiry", emailBody)}
                  className="flex items-center gap-3 p-4 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors"
                  data-testid="signup-contact-email"
                >
                  <Mail className="w-5 h-5 text-[#D97706]" />
                  <div>
                    <div className="text-xs text-white/60">Email</div>
                    <div className="text-sm font-medium break-all">
                      {SITE.email}
                    </div>
                  </div>
                </a>
              </div>
            </div>

            <div className="lg:col-span-5 relative min-h-[280px] lg:min-h-0">
              <img
                src={studentImage}
                alt="Cartoon illustration of a smiling primary school student studying at a desk"
                className="absolute inset-0 w-full h-full object-contain bg-white p-4"
                data-testid="signup-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1E3A8A]/40 via-transparent to-transparent lg:bg-none" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
